"use client"

import type React from "react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ShoppingBag, Eye, Heart, Star, Sparkles } from "lucide-react"
import { OrderModal } from "@/components/order-modal"
import type { Product } from "@/lib/api"
import { useLanguage } from "@/lib/language-context"
import { cn } from "@/lib/utils"

interface ProductCardProps {
  product: Product
  index?: number
  viewMode?: "grid" | "list" // viewMode prop qo'shildi
}

function getImageUrl(product: Product): string {
  if (product.image_url) return product.image_url
  if (product.image) return product.image
  return `/placeholder.svg?height=500&width=500&query=${encodeURIComponent(product.name + " natural herbal medicine")}`
}

export function ProductCard({ product, index = 0, viewMode = "grid" }: ProductCardProps) {
  const { t } = useLanguage()
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleOrderClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOrderModalOpen(true)
  }

  const handleLikeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsLiked(!isLiked)
  }

  if (viewMode === "list") {
    return (
      <>
        <Link to={`/products/${product.slug}`} className="block">
          <div className="group flex gap-4 bg-card rounded-xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg p-4">
            {/* Image */}
            <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={getImageUrl(product) || "/placeholder.svg"}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {product.stock === 0 && (
                <Badge className="absolute top-2 left-2 bg-destructive/90 text-destructive-foreground text-xs">
                  {t("home", "out_of_stock")}
                </Badge>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col justify-between py-1">
              <div>
                <p className="text-xs font-semibold text-primary mb-1 uppercase tracking-wide">
                  {product.category_name}
                </p>
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-1">
                  {product.name}
                </h3>
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">(24)</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
              </div>

              <div className="flex items-center justify-between mt-3">
                <div>
                  <span className="text-xl font-bold text-primary">{Number(product.price).toLocaleString()}</span>
                  <span className="text-sm text-muted-foreground ml-1">{t("home", "currency")}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="bg-transparent" onClick={handleLikeClick}>
                    <Heart className={cn("w-4 h-4", isLiked && "fill-red-500 text-red-500")} />
                  </Button>
                  <Button size="sm" onClick={handleOrderClick} disabled={product.stock === 0}>
                    <ShoppingBag className="w-4 h-4 mr-1" />
                    {t("home", "order_button")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Link>

        <OrderModal product={product} isOpen={isOrderModalOpen} onClose={() => setIsOrderModalOpen(false)} />
      </>
    )
  }

  // Grid view (default)
  return (
    <>
      <Link to={`/products/${product.slug}`} className="block">
        <div
          className="group relative bg-card rounded-2xl lg:rounded-3xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Image Container */}
          <div className="relative aspect-square overflow-hidden bg-muted/50">
            <img
              src={getImageUrl(product) || "/placeholder.svg"}
              alt={product.name}
              className={cn(
                "w-full h-full object-cover transition-all duration-700",
                isHovered ? "scale-110" : "scale-100",
              )}
            />

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
              {product.stock === 0 && (
                <Badge className="bg-destructive/90 text-destructive-foreground backdrop-blur-sm shadow-lg">
                  {t("home", "out_of_stock")}
                </Badge>
              )}
              {product.stock > 0 && product.stock < 10 && (
                <Badge className="bg-amber-500/90 text-white backdrop-blur-sm shadow-lg">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t("home", "low_stock")}
                </Badge>
              )}
            </div>

            {/* Quick Actions */}
            <div
              className={cn(
                "absolute top-3 right-3 flex flex-col gap-2 z-10 transition-all duration-300",
                isHovered ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4",
              )}
            >
              <button
                onClick={handleLikeClick}
                className={cn(
                  "w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg backdrop-blur-sm",
                  isLiked
                    ? "bg-red-500 text-white scale-110"
                    : "bg-white/90 text-foreground hover:bg-primary hover:text-primary-foreground hover:scale-110",
                )}
              >
                <Heart className={cn("w-5 h-5 transition-transform", isLiked && "fill-current animate-pulse")} />
              </button>
              <div className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-lg backdrop-blur-sm hover:scale-110">
                <Eye className="w-5 h-5" />
              </div>
            </div>

            {/* Quick Buy Button */}
            <div
              className={cn(
                "absolute bottom-3 left-3 right-3 z-10 transition-all duration-300",
                isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
              )}
            >
              <Button
                className="w-full shadow-xl backdrop-blur-sm gap-2 py-5"
                onClick={handleOrderClick}
                disabled={product.stock === 0}
              >
                <ShoppingBag className="h-4 w-4" />
                {t("home", "order_button")}
              </Button>
            </div>

            {/* Gradient overlay on hover */}
            <div
              className={cn(
                "absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent transition-opacity duration-300",
                isHovered ? "opacity-100" : "opacity-0",
              )}
            />
          </div>

          {/* Content */}
          <div className="p-4 lg:p-5">
            {/* Category */}
            <p className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">{product.category_name}</p>

            {/* Name */}
            <h3 className="font-bold text-lg mb-2 line-clamp-1 group-hover:text-primary transition-colors">
              {product.name}
            </h3>

            {/* Rating */}
            <div className="flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
              <span className="text-xs text-muted-foreground ml-2">(24 {t("home", "reviews")})</span>
            </div>

            {/* Price and Stock */}
            <div className="flex items-center justify-between">
              <div>
                <span className="text-2xl font-bold text-primary">{Number(product.price).toLocaleString()}</span>
                <span className="text-sm font-medium text-muted-foreground ml-1">{t("home", "currency")}</span>
              </div>
              {product.stock > 0 && (
                <span className="text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full font-medium">
                  {product.stock} {t("home", "in_stock")}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>

      <OrderModal product={product} isOpen={isOrderModalOpen} onClose={() => setIsOrderModalOpen(false)} />
    </>
  )
}
